import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  X,
  FileText,
  Upload,
  Calendar,
  Loader2,
  Search,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Award,
  CreditCard,
  Clock,
  Archive,
  ChevronLeft,
  Hash,
} from 'lucide-react';
import AbsenceRecordsModal from '../AbsenceRecordsModal';
import {
  getEmployeeById,
  getEmployeeImage,
  getAllOffboardings,
} from '../../services/HRstaff';
import { getEmployeeAbsences } from '../../services/absenceAPIs';
import { getEmployeeLeaveBalance } from '../../services/leavesAPIs';
import { getAllDocuments, createDocument } from '../../services/documentAPIs';
import { getEmployeeNotes } from '../../services/NoteAPIs';
import DateFormatter from '../DateFormatter';
import DocumentDetailsModal from '../DViews/DocDetailsModal';
import '../ViewProfile.css';

/* ─── Helpers ──────────────────────────────────────────────────────── */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'N/A';
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  } catch {
    return 'N/A';
  }
};

const formatPeriodWorked = (start, end) => {
  if (!start || !end) return 'N/A';
  const s = new Date(start), e = new Date(end);
  if (isNaN(s) || isNaN(e)) return 'N/A';
  const days = Math.floor(Math.abs(e - s) / 86400000);
  if (days < 31) return `${days} day${days !== 1 ? 's' : ''}`;
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  return [years > 0 && `${years}y`, months > 0 && `${months}m`].filter(Boolean).join(' ') || `${days}d`;
};

const calcWorkDays = (s, e) => {
  if (!s || !e) return 0;
  const start = new Date(s), end = new Date(e);
  if (isNaN(start) || isNaN(end)) return 0;
  let count = 0, cur = new Date(start);
  while (cur <= end) { if (cur.getDay() !== 0) count++; cur.setDate(cur.getDate() + 1); }
  return count;
};

/* ─── Component ─────────────────────────────────────────────────────── */
const InactiveViewProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Dark mode — app uses data-theme="dark" on <html>
  const [isDark, setIsDark] = useState(
    () => document.documentElement.getAttribute('data-theme') === 'dark'
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.getAttribute('data-theme') === 'dark')
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  // Colour tokens — mirrors ViewProfile's dm object
  const dm = {
    card: isDark ? '#1e293b' : '#ffffff',
    surface: isDark ? '#0f172a' : '#F3F4F6',
    surfaceAlt: isDark ? '#1e293b' : '#F9FAFB',
    border: isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB',
    textPrimary: isDark ? '#f1f5f9' : '#111827',
    textSecondary: isDark ? '#94a3b8' : '#6B7280',
    textMuted: isDark ? '#64748b' : '#9CA3AF',
  };

  /* state */
  const [employee, setEmployee] = useState(null);
  const [cnss, setCnss] = useState('N/A');
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [exitDate, setExitDate] = useState(null);
  const [offboardingReason, setOffboardingReason] = useState('N/A');
  const [offboardingAdditionalNotes, setOffboardingAdditionalNotes] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activeTab, setActiveTab] = useState('attendance');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [latenessRate, setLatenessRate] = useState(0);
  const [sicknessRate, setSicknessRate] = useState(0);
  const [personalRate, setPersonalRate] = useState(0);
  const [ratesLoading, setRatesLoading] = useState(true);

  const [vacationDaysTaken, setVacationDaysTaken] = useState(0);
  const [sickDays, setSickDays] = useState(0);
  const [personalDays, setPersonalDays] = useState(0);
  const [latenessDays, setLatenessDays] = useState(0);

  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('All');
  const [docSearchTerm, setDocSearchTerm] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadForm, setUploadForm] = useState({ type: 'Contract', description: '', file: null });
  const [uploading, setUploading] = useState(false);
  const [docError, setDocError] = useState(null);
  const [docSuccess, setDocSuccess] = useState(null);

  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [selectedAbsenceType, setSelectedAbsenceType] = useState(null);

  /* auto-clear notifications */
  useEffect(() => {
    if (docError || docSuccess) {
      const t = setTimeout(() => { setDocError(null); setDocSuccess(null); }, 5000);
      return () => clearTimeout(t);
    }
  }, [docError, docSuccess]);

  /* profile image */
  useEffect(() => {
    if (!employee?.id || !employee.hasImage) return;
    getEmployeeImage(employee.id).then(setProfileImageUrl).catch(() => { });
  }, [employee?.id, employee?.hasImage]);

  /* main fetch */
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    (async () => {
      try {
        const [emp, offboardings] = await Promise.all([getEmployeeById(id), getAllOffboardings()]);
        const matchingOffboardings = offboardings.filter(o => o.employee?.employeeId === id || o.employeeId === id);
        let match = matchingOffboardings[0];
        if (!match) {
          const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
          match = offboardings.find(r => `${r.employeeFirstName || ''} ${r.employeeLastName || ''}`.trim() === fullName);
        }
        const offboardingMatches = matchingOffboardings.length > 0
          ? matchingOffboardings
          : offboardings.filter(r => `${r.employeeFirstName || ''} ${r.employeeLastName || ''}`.trim() === `${emp.firstName || ''} ${emp.lastName || ''}`.trim());
        const exit = match?.exitDate || null;
        const joinDate = emp.dateOfJoining || emp.joinDate;
        setExitDate(exit);
        setOffboardingReason(match?.reasonForDeparture || 'N/A');
        setOffboardingAdditionalNotes(
          offboardingMatches
            .map(record => record.additionalNotes || record.notes || '')
            .filter(note => note && note.trim() !== '')
        );
        setCnss(emp.cnss || 'N/A');
        const extractInactivePositions = (employeeObj) => {
          if (!employeeObj) return 'N/A';
          if (employeeObj.positions && Array.isArray(employeeObj.positions) && employeeObj.positions.length > 0) {
            const valid = employeeObj.positions.filter(p => p && p.positionName);
            if (valid.length > 0) return valid.map(p => p.positionName).join('; ');
          }
          if (employeeObj.contract && Array.isArray(employeeObj.contract)) {
            for (const c of employeeObj.contract) {
              if (c.employee?.positions && Array.isArray(c.employee.positions)) {
                const valid = c.employee.positions.filter(p => p && p.positionName);
                if (valid.length > 0) return valid.map(p => p.positionName).join('; ');
              }
            }
          }
          if (employeeObj.payrolls && Array.isArray(employeeObj.payrolls)) {
            for (const p of employeeObj.payrolls) {
              if (p.employee?.positions && Array.isArray(p.employee.positions)) {
                const valid = p.employee.positions.filter(pos => pos && pos.positionName);
                if (valid.length > 0) return valid.map(pos => pos.positionName).join('; ');
              }
            }
          }
          if (employeeObj.balances && Array.isArray(employeeObj.balances)) {
            for (const b of employeeObj.balances) {
              if (b.employee?.positions && Array.isArray(b.employee.positions)) {
                const valid = b.employee.positions.filter(pos => pos && pos.positionName);
                if (valid.length > 0) return valid.map(pos => pos.positionName).join('; ');
              }
            }
          }
          const single = employeeObj.positionName || employeeObj.position || employeeObj.jobTitle || employeeObj.job || employeeObj.role || employeeObj.title;
          if (single) return single;
          return 'N/A';
        };

        setEmployee({
          id: emp.employeeId || id,
          name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'N/A',
          firstName: emp.firstName || '',
          lastName: emp.lastName || '',
          hasImage: !!emp.image,
          position: extractInactivePositions(emp),
          office: emp.office?.name || emp.office || 'N/A',
          user: { email: emp.user?.email || emp.email || 'N/A' },
          cin: emp.cin || 'N/A',
          gender: emp.gender || 'N/A',
          phone: emp.phone || 'N/A',
          dob: emp.dob || null,
          diploma: emp.diploma || 'N/A',
          joinDate,
          exitDate: exit,
          totalWorkDays: calcWorkDays(joinDate, exit),
        });
        setNotes(await getEmployeeNotes(id).catch(() => []));
      } catch (e) {
        console.error(e);
        setError('Failed to load employee data.');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [id]);

  /* absence / rates */
  useEffect(() => {
    if (!id || !employee?.joinDate || !employee?.exitDate) return;
    setRatesLoading(true);
    (async () => {
      try {
        const absences = await getEmployeeAbsences(id);
        const vacation = absences.filter(r => r.ty_pe?.toUpperCase() === 'ANNUAL_LEAVE' && r.status?.toUpperCase() === 'APPROVED');
        const sick = absences.filter(r => r.ty_pe?.toUpperCase() === 'SICKNESS');
        const personal = absences.filter(r => ['PERSONAL_COMMITMENT', 'PERSONAL'].includes(r.ty_pe?.toUpperCase()));
        const lateness = absences.filter(r => r.ty_pe?.toUpperCase() === 'LATENESS');
        const vDays = vacation.reduce((s, r) => s + (r.days || r.durationInDays || 0), 0);
        const sDays = sick.reduce((s, r) => s + (r.days || r.durationInDays || 0), 0);
        setVacationDaysTaken(vDays);
        setSickDays(sDays);
        setPersonalDays(personal.length);
        setLatenessDays(lateness.length);
        const tw = employee.totalWorkDays || 1;
        setLatenessRate(parseFloat(((lateness.length / tw) * 100).toFixed(2)));
        setSicknessRate(parseFloat(((sDays / tw) * 100).toFixed(2)));
        setPersonalRate(parseFloat(((personal.length / tw) * 100).toFixed(2)));
      } catch (e) {
        console.error(e);
      } finally {
        setRatesLoading(false);
      }
    })();
  }, [id, employee?.joinDate, employee?.exitDate]);

  /* documents */
  useEffect(() => {
    if (!id) return;
    getAllDocuments()
      .then(all => setDocuments(all.filter(d => d.employee?.employeeId === id)))
      .catch(() => { });
  }, [id]);

  const fetchDocuments = () =>
    getAllDocuments()
      .then(all => setDocuments(all.filter(d => d.employee?.employeeId === id)))
      .catch(() => { });

  const handleUploadChange = e => {
    const { name, value, files } = e.target;
    setUploadForm(p => ({ ...p, [name]: files ? files[0] : value }));
  };

  const handleUploadSubmit = async () => {
    if (!uploadForm.description || !uploadForm.file) { setDocError('Fill all required fields.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('document', new Blob([JSON.stringify({ employee: { employeeId: id }, type: uploadForm.type, description: uploadForm.description })], { type: 'application/json' }));
      fd.append('file', uploadForm.file);
      const newDoc = await createDocument(fd);
      setDocuments(p => [...p, newDoc]);
      setDocSuccess('Document uploaded!');
      setUploadForm({ type: 'Contract', description: '', file: null });
      setShowUploadForm(false);
    } catch { setDocError('Upload failed.'); }
    finally { setUploading(false); }
  };

  /* ── loading / error ── */
  if (isLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '1rem' }}>
      <Loader2 size={48} style={{ animation: 'spin 1s linear infinite', color: '#6B7280' }} />
      <p style={{ color: '#6B7280' }}>Loading archive profile…</p>
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '1rem' }}>
      <AlertCircle size={48} color="#EF4444" />
      <p>{error}</p>
      <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1rem', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>Retry</button>
    </div>
  );

  if (!employee) return null;

  const filteredDocuments = documents.filter(doc => {
    const matchType = selectedDocumentType === 'All' || doc.type === selectedDocumentType;
    const matchSearch = !docSearchTerm || doc.description?.toLowerCase().includes(docSearchTerm.toLowerCase());
    return matchType && matchSearch;
  });

  /* ── rate card helper ── */
  const rateCard = (label, rate) => {
    const isHigh = rate > 2;
    const cls = isHigh ? 'iap-rate-high rate-card' : 'iap-rate-low rate-card';
    const bg = isDark ? (isHigh ? '#3f1415' : '#1e293b') : (isHigh ? '#FEE2E2' : '#F3F4F6');
    const borderColor = isHigh ? '#EF4444' : '#9CA3AF';
    const textColor = isDark ? (isHigh ? '#fca5a5' : '#94a3b8') : (isHigh ? '#991B1B' : '#4B5563');
    return (
      <div className={cls} style={{ backgroundColor: bg, border: `1px solid ${borderColor}`, borderRadius: '0.5rem', padding: '0.5rem 1rem', minWidth: '120px' }}>
        <p style={{ fontSize: '0.75rem', color: textColor, margin: 0 }}>{label}</p>
        <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: textColor, margin: 0 }}>
          {ratesLoading ? '...' : `${rate}%`}
        </p>
      </div>
    );
  };

  /* ── Tabs ── same tab structure as ViewProfile ── */
  const tabs = [
    { key: 'attendance', label: 'Attendance', icon: Clock },
    { key: 'documents', label: `Documents${documents.length > 0 ? ` (${documents.length})` : ''}`, icon: FileText },
    { key: 'notes', label: `Notes${notes.length > 0 ? ` (${notes.length})` : ''}`, icon: FileText },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'transparent',
        padding: '10px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        overflowY: 'auto',
        maxHeight: '100vh',
      }}
    >
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes slideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .fade-in { animation: slideIn 0.3s ease-out; }

        /* ── Restore backgrounds nuked by darkmode.css ───────────────────── */
        /* darkmode.css sets background-color:transparent on ALL divs under [data-theme=dark]. */
        /* We use high-specificity selectors to re-apply our intended colours. */
        [data-theme='dark'] .iap-card          { background-color: #1e293b !important; }
        [data-theme='dark'] .iap-surface       { background-color: #0f172a !important; }
        [data-theme='dark'] .iap-surface-alt   { background-color: #1e293b !important; }
        [data-theme='dark'] .iap-attendance-vacation  { background-color: #1e3a5f !important; }
        [data-theme='dark'] .iap-attendance-sick      { background-color: #3f1415 !important; }
        [data-theme='dark'] .iap-attendance-personal  { background-color: #2e1a4b !important; }
        [data-theme='dark'] .iap-attendance-lateness  { background-color: #2d2000 !important; }
        [data-theme='dark'] .iap-strip         { background-color: #1e293b !important; }
        [data-theme='dark'] .iap-note          { background-color: #0f172a !important; }
        [data-theme='dark'] .iap-doc-card      { background-color: #1e293b !important; }
        [data-theme='dark'] .iap-rate-low      { background-color: #1e293b !important; }
        [data-theme='dark'] .iap-rate-high     { background-color: #3f1415 !important; }
        [data-theme='dark'] .iap-timeline-item { background-color: #1e293b !important; }
        [data-theme='dark'] .iap-upload-form   { background-color: #0f172a !important; }
        [data-theme='dark'] .iap-tab-wrap      { background-color: #1e293b !important; }

        @media (max-width: 768px) {
          .profile-header-content { flex-direction: column; align-items: center; gap: 1rem; }
          .profile-info-grid { grid-template-columns: 1fr; }
          .rate-cards-container { flex-direction: column; align-items: center; gap: 0.5rem; }
          .rate-card { min-width: 100%; padding: 0.5rem; }
          .profile-image { width: 80px !important; height: 80px !important; }
          .profile-name { font-size: 1.5rem !important; }
          .archive-stats-strip { flex-direction: column !important; gap: 0.5rem !important; }
        }
      `}</style>

      {/* ── PROFILE CARD ── */}
      <div style={{ zIndex: 999, paddingTop: 0, marginBottom: '2rem' }}>
        <div className="iap-card" style={{ backgroundColor: dm.card, borderRadius: '1rem', padding: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>

          {/* Back link */}
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.25rem',
              background: 'none', border: 'none', cursor: 'pointer',
              color: dm.textSecondary, fontSize: '0.875rem', marginBottom: '1.25rem', padding: 0,
            }}
          >
            <ChevronLeft size={16} /> Back to Archive
          </button>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2rem' }} className="profile-header-content">

            {/* ── Avatar — same size as ViewProfile but with grey border + archive dot ── */}
            <div style={{ position: 'relative' }}>
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt={`${employee.name}'s profile`}
                  style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', border: '4px solid #9CA3AF', filter: 'grayscale(20%)' }}
                  className="profile-image"
                  onError={() => setProfileImageUrl(null)}
                />
              ) : (
                <div
                  style={{ width: 120, height: 120, borderRadius: '50%', backgroundColor: '#6B7280', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', fontWeight: 'bold', border: '4px solid #9CA3AF' }}
                  className="profile-image"
                >
                  {employee.firstName?.[0]}{employee.lastName?.[0]}
                </div>
              )}
              {/* Inactive dot — grey instead of green */}
              <div style={{ position: 'absolute', bottom: 5, right: 5, width: 20, height: 20, borderRadius: '50%', backgroundColor: '#6B7280', border: `3px solid ${dm.card}` }} />
            </div>

            {/* ── Name, position, rate cards ── */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                    <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: dm.textPrimary, margin: 0 }} className="profile-name">
                      {employee.name}
                    </h1>
                    {/* ── Archived badge ── */}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                      backgroundColor: isDark ? '#374151' : '#F3F4F6',
                      color: isDark ? '#D1D5DB' : '#4B5563',
                      border: `1px solid ${isDark ? '#4B5563' : '#D1D5DB'}`,
                      padding: '2px 10px', borderRadius: '999px',
                      fontSize: '0.78rem', fontWeight: 700,
                    }}>
                      <Archive size={12} /> Archived
                    </span>
                  </div>
                  <p style={{ fontSize: '1.25rem', color: dm.textSecondary, marginBottom: '1rem' }} className="profile-position">
                    {employee.position}
                  </p>
                </div>

                {/* Rate cards — same layout as ViewProfile, muted palette */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1rem' }} className="rate-cards-wrapper">
                  {/* Exit summary strip (replaces year buttons for inactive) */}
                  <div className="iap-strip archive-stats-strip" style={{ display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: isDark ? '#1e293b' : '#F9FAFB', border: `1px solid ${isDark ? '#334155' : '#E5E7EB'}`, borderRadius: '0.75rem', padding: '0.5rem 1rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Calendar size={14} color="#6B7280" />
                      <span style={{ fontSize: '0.8rem', color: dm.textSecondary }}>Joined:</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: dm.textPrimary }}>{formatDate(employee.joinDate)}</span>
                    </div>
                    <div style={{ width: 1, height: 18, backgroundColor: dm.border }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Archive size={14} color="#EF4444" />
                      <span style={{ fontSize: '0.8rem', color: dm.textSecondary }}>Exit:</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#EF4444' }}>{formatDate(exitDate)}</span>
                    </div>
                    <div style={{ width: 1, height: 18, backgroundColor: dm.border }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <Clock size={14} color="#6B7280" />
                      <span style={{ fontSize: '0.8rem', color: dm.textSecondary }}>Period:</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: dm.textPrimary }}>{formatPeriodWorked(employee.joinDate, exitDate)}</span>
                    </div>
                    {offboardingReason && offboardingReason !== 'N/A' && (
                      <>
                        <div style={{ width: 1, height: 18, backgroundColor: dm.border }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <span style={{ fontSize: '0.8rem', color: dm.textSecondary }}>Reason:</span>
                          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: dm.textPrimary, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={offboardingReason}>{offboardingReason}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Rate cards — same layout as ViewProfile */}
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'flex-end' }} className="rate-cards-container">
                    {rateCard('Lateness Rate', latenessRate)}
                    {rateCard('Sickness Rate', sicknessRate)}
                    {rateCard('Personal Rate', personalRate)}
                  </div>
                </div>
              </div>

              {/* ── Info grid — exactly like ViewProfile ── */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginTop: '1.5rem' }} className="profile-info-grid">
                {[
                  { Icon: Mail, label: 'Email', value: employee.user?.email },
                  { Icon: Phone, label: 'Phone', value: employee.phone },
                  { Icon: MapPin, label: 'Office', value: employee.office },
                  { Icon: Calendar, label: 'Date of Birth', value: formatDate(employee.dob) },
                  { Icon: CreditCard, label: 'CIN', value: employee.cin },
                  { Icon: Hash, label: 'CNSS', value: cnss },
                  { Icon: User, label: 'Gender', value: employee.gender },
                  { Icon: Award, label: 'Diploma', value: employee.diploma },
                ].map(({ Icon, label, value }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Icon size={18} color="#6B7280" />
                    <div>
                      <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.125rem', margin: 0 }}>{label}</p>
                      <p style={{ fontSize: '0.875rem', fontWeight: '500', color: dm.textPrimary, margin: 0 }}>{value || 'N/A'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── TABS ── */}
      <div className="iap-card" style={{ backgroundColor: dm.card, borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div className="iap-tab-wrap viewprofile-profile-tabs" style={{ backgroundColor: isDark ? '#1e293b' : '#f8fafc', borderBottom: `1px solid ${dm.border}` }}>
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={`viewprofile-tab-btn${activeTab === key ? ' active' : ''}`}
              onClick={() => setActiveTab(key)}
              style={activeTab === key
                ? { color: '#3b82f6', borderBottom: '3px solid #3b82f6', backgroundColor: dm.card }
                : { color: dm.textSecondary }}
            >
              <Icon size={18} className="viewprofile-tab-icon" />
              {label}
            </button>
          ))}
        </div>

        {/* ── TAB CONTENT ── */}
        <div className="viewprofile-tab-content viewprofile-fade-in">

          {/* ── ATTENDANCE ── */}
          {activeTab === 'attendance' && (
            <div>
              {/* Attendance cards — same grid/style as ViewProfile attendance section */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }} className="attendance-grid">
                {[
                  { title: 'Vacation Days', emoji: '🏖️', value: vacationDaysTaken, color: '#3B82F6', bg: isDark ? '#1e3a5f' : '#EFF6FF', type: 'ANNUAL_LEAVE', cls: 'iap-attendance-vacation' },
                  { title: 'Sick Days', emoji: '🤒', value: sickDays, color: '#EF4444', bg: isDark ? '#3f1415' : '#FEF2F2', type: 'SICKNESS', cls: 'iap-attendance-sick' },
                  { title: 'Personal Days', emoji: '🎒', value: personalDays, color: '#8B5CF6', bg: isDark ? '#2e1a4b' : '#F5F3FF', type: 'PERSONAL', cls: 'iap-attendance-personal' },
                  { title: 'Late Arrivals', emoji: '⏰', value: latenessDays, color: '#F59E0B', bg: isDark ? '#2d2000' : '#FFFBEB', type: 'LATENESS', cls: 'iap-attendance-lateness' },
                ].map(card => (
                  <div
                    key={card.type}
                    onClick={() => { setSelectedAbsenceType(card.type); setShowAbsenceModal(true); }}
                    className={`attendance-card ${card.cls}`}
                    style={{
                      backgroundColor: card.bg,
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      cursor: 'pointer',
                      border: `1px solid ${card.color}22`,
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 8px 24px ${card.color}22`; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      <div className="attendance-icon-container" style={{ width: 44, height: 44, borderRadius: '0.5rem', backgroundColor: card.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem' }}>
                        {card.emoji}
                      </div>
                      <div>
                        <p style={{ fontSize: '0.8rem', color: dm.textSecondary, margin: 0 }}>{card.title}</p>
                        <p style={{ fontSize: '1.75rem', fontWeight: 800, color: card.color, margin: 0, lineHeight: 1 }}>{card.value ?? '…'}</p>
                      </div>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(((card.value || 0) / 30) * 100, 100)}%`, backgroundColor: card.color, borderRadius: 2, transition: 'width 0.5s ease' }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Time overview card — same as ViewProfile's time section */}
              <div className="iap-surface" style={{ backgroundColor: isDark ? '#0f172a' : '#F9FAFB', border: `1px solid ${dm.border}`, borderRadius: '0.75rem', padding: '1.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: dm.textPrimary, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Archive size={18} color="#6B7280" /> Employment Timeline
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }} className="time-overview-grid">
                  {[
                    { label: 'Date Joined', value: formatDate(employee.joinDate) },
                    { label: 'Exit Date', value: formatDate(exitDate), valueStyle: { color: '#EF4444', fontWeight: 700 } },
                    { label: 'Total Working Days', value: `${employee.totalWorkDays} days` },
                    { label: 'Period Worked', value: formatPeriodWorked(employee.joinDate, exitDate) },
                    { label: 'Offboarding Reason', value: offboardingReason },
                  ].map(({ label, value, valueStyle }) => (
                    <div key={label} className="iap-timeline-item" style={{ padding: '1rem', backgroundColor: dm.card, borderRadius: '0.5rem', border: `1px solid ${dm.border}` }}>
                      <p style={{ fontSize: '0.75rem', color: dm.textSecondary, marginBottom: '0.25rem', margin: '0 0 4px' }}>{label}</p>
                      <p style={{ fontSize: '0.9rem', fontWeight: 600, color: dm.textPrimary, margin: 0, ...valueStyle }}>{value || 'N/A'}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── DOCUMENTS ── */}
          {activeTab === 'documents' && (
            <div>
              {docError && <div style={{ backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.75rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between' }}><span>{docError}</span><button onClick={() => setDocError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button></div>}
              {docSuccess && <div style={{ backgroundColor: '#D1FAE5', color: '#065F46', padding: '0.75rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between' }}><span>{docSuccess}</span><button onClick={() => setDocSuccess(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button></div>}

              {/* controls */}
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  value={selectedDocumentType}
                  onChange={e => setSelectedDocumentType(e.target.value)}
                  style={{ padding: '0.5rem 1rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', backgroundColor: dm.card, color: dm.textPrimary, fontSize: '0.875rem' }}
                >
                  {['All', 'Contract', 'National ID', 'Diploma', 'Payroll', 'Other'].map(t => <option key={t} value={t}>{t === 'All' ? 'All Documents' : t}</option>)}
                </select>
                <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
                  <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                  <input
                    placeholder="Search documents…"
                    value={docSearchTerm}
                    onChange={e => setDocSearchTerm(e.target.value)}
                    style={{ width: '100%', padding: '0.5rem 0.75rem 0.5rem 2rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', backgroundColor: dm.card, color: dm.textPrimary, fontSize: '0.875rem', outline: 'none' }}
                  />
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span style={{ fontSize: '0.875rem', color: dm.textSecondary }}>{filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}</span>
                  <button
                    onClick={() => setShowUploadForm(p => !p)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}
                  >
                    <Upload size={16} /> Upload
                  </button>
                </div>
              </div>

              {/* upload form */}
              {showUploadForm && (
                <div className="iap-upload-form" style={{ backgroundColor: isDark ? '#0f172a' : '#F9FAFB', border: `1px solid ${dm.border}`, borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 1rem', color: dm.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Upload size={16} /> Upload Document</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <select name="type" value={uploadForm.type} onChange={handleUploadChange} style={{ padding: '0.5rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', backgroundColor: dm.card, color: dm.textPrimary }}>
                      {['Contract', 'National ID', 'Diploma', 'Other'].map(t => <option key={t}>{t}</option>)}
                    </select>
                    <input placeholder="Description *" name="description" value={uploadForm.description} onChange={handleUploadChange} style={{ padding: '0.5rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', backgroundColor: dm.card, color: dm.textPrimary }} />
                    <input type="file" name="file" onChange={handleUploadChange} accept=".pdf,.doc,.docx,.txt,.jpg,.png" style={{ padding: '0.35rem', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', backgroundColor: dm.card, color: dm.textPrimary }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
                    <button onClick={() => setShowUploadForm(false)} style={{ padding: '0.5rem 1rem', background: 'none', border: `1px solid ${dm.border}`, borderRadius: '0.5rem', cursor: 'pointer', color: dm.textSecondary }}>Cancel</button>
                    <button onClick={handleUploadSubmit} disabled={uploading} style={{ padding: '0.5rem 1rem', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600 }}>
                      {uploading ? 'Uploading…' : 'Upload'}
                    </button>
                  </div>
                </div>
              )}

              {/* document cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }} className="document-grid">
                {filteredDocuments.length > 0 ? filteredDocuments.map(doc => (
                  <div
                    key={doc.id}
                    onClick={() => setSelectedDoc(doc)}
                    className="iap-doc-card document-card"
                    style={{ backgroundColor: dm.card, border: `1px solid ${dm.border}`, borderRadius: '0.75rem', padding: '1.25rem', cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = '#3B82F6'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = dm.border; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem', color: dm.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%' }}>{doc.description}</span>
                      <span style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8', padding: '2px 8px', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{doc.type}</span>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: dm.textSecondary, margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Calendar size={12} /> {formatDate(doc.uploadDate)}
                    </p>
                    <button style={{ width: '100%', padding: '0.4rem', backgroundColor: 'transparent', border: `1px solid ${dm.border}`, borderRadius: '0.375rem', cursor: 'pointer', color: dm.textSecondary, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}>
                      <FileText size={13} /> View File
                    </button>
                  </div>
                )) : (
                  <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: dm.textMuted }}>
                    <FileText size={48} style={{ opacity: 0.2, marginBottom: 12 }} />
                    <p style={{ fontWeight: 600, margin: '0 0 0.5rem', fontSize: '1rem' }}>No Documents Found</p>
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>Upload documents to keep a complete archived record</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── NOTES ── */}
          {activeTab === 'notes' && (
            <div>
              {offboardingAdditionalNotes.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, color: dm.textPrimary, marginBottom: '0.75rem' }}>
                    Offboarding Additional Notes
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {offboardingAdditionalNotes.map((note, index) => (
                      <div
                        key={`${index}-${note.slice(0, 20)}`}
                        className="iap-note"
                        style={{ backgroundColor: isDark ? '#0f172a' : '#F9FAFB', border: `1px solid ${dm.border}`, borderRadius: '0.75rem', padding: '1rem 1.25rem' }}
                      >
                        <p style={{ margin: 0, fontSize: '0.875rem', color: dm.textPrimary, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                          {note}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {notes.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {notes.map(note => (
                    <div
                      key={note.id}
                      className="iap-note"
                      style={{ backgroundColor: isDark ? '#0f172a' : '#F9FAFB', border: `1px solid ${dm.border}`, borderRadius: '0.75rem', padding: '1.25rem', transition: 'box-shadow 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <strong style={{ color: dm.textPrimary, fontSize: '0.9rem' }}>{note.createdBy || note.managerReview || 'Unknown'}</strong>
                        <span style={{ fontSize: '0.8rem', color: dm.textSecondary }}><DateFormatter date={note.createdDate || note.incidentDate} /></span>
                      </div>
                      {note.violationType && (
                        <span style={{ display: 'inline-block', backgroundColor: isDark ? '#1e293b' : '#E5E7EB', color: dm.textSecondary, padding: '2px 8px', borderRadius: '999px', fontSize: '0.75rem', marginBottom: '0.75rem' }}>{note.violationType}</span>
                      )}
                      <p style={{ margin: 0, fontSize: '0.875rem', color: dm.textPrimary, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{note.description || note.content}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '4rem', color: dm.textMuted }}>
                  <FileText size={48} style={{ opacity: 0.2, marginBottom: 12 }} />
                  <p style={{ fontWeight: 600, margin: '0 0 0.5rem', fontSize: '1rem' }}>No Notes</p>
                  <p style={{ margin: 0, fontSize: '0.875rem' }}>Notes added during this employee's tenure will appear here</p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* ── Modals ── */}
      <AbsenceRecordsModal
        open={showAbsenceModal}
        onClose={() => { setShowAbsenceModal(false); setSelectedAbsenceType(null); }}
        employeeId={employee.id}
        absenceType={selectedAbsenceType}
        employeeName={employee.name}
        selectedYear="All Time"
      />
      {selectedDoc && (
        <DocumentDetailsModal
          selectedDocument={selectedDoc}
          setSelectedDocument={setSelectedDoc}
          fetchDocuments={fetchDocuments}
          isEditMode={false}
          setIsEditMode={setIsEditMode}
        />
      )}
    </div>
  );
};

export default InactiveViewProfile;
